/**
 * NPS one-click landing page (#16 — email-embedded score links).
 *
 * Email links look like:
 *   /nps?score=9&email=customer@x.com&tenant=tnt_x&context=board_pack
 *
 * On load, the page POSTs to /api/public/nps-pulse and shows a branded
 * thank-you. If the link is missing required params, an inline 0-10
 * picker lets the visitor still submit.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Seo } from "@/components/Seo";
import { Loader2, Smile, ThumbsUp, MessageSquare } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function useQuery() {
  const { search } = useLocation();
  return new URLSearchParams(search);
}

export default function NpsLanding() {
  const { t } = useTranslation();
  const q = useQuery();
  const initialScore = parseInt(q.get("score"), 10);
  const email = q.get("email") || "";
  const tenant = q.get("tenant") || "";
  const context = q.get("context") || "governance_pack";

  const [score, setScore] = useState(
    Number.isInteger(initialScore) && initialScore >= 0 && initialScore <= 10
      ? initialScore : null,
  );
  const [submitted, setSubmitted] = useState(null);
  const [comment, setComment] = useState("");
  const [showComment, setShowComment] = useState(false);
  const [posting, setPosting] = useState(false);

  const submit = async (s, extraComment = "") => {
    setPosting(true);
    try {
      const r = await axios.post(`${API}/api/public/nps-pulse`, {
        score: s,
        email: email || null,
        tenant_id: tenant || null,
        context,
        comment: extraComment || null,
      });
      setSubmitted({ score: s, category: r.data.category });
    } catch (e) {
      setSubmitted({ score: s, category: "passive", error: true });
    } finally {
      setPosting(false);
    }
  };

  useEffect(() => {
    if (score !== null && !submitted) submit(score);
  }, []);

  const sendComment = async () => {
    if (!comment.trim() || !submitted) return;
    await submit(submitted.score, comment.trim());
    setShowComment(false);
  };

  const categoryLabel = (cat) => {
    if (cat === "promoter") return t("nps.categoryPromoter");
    if (cat === "detractor") return t("nps.categoryDetractor");
    return t("nps.categoryPassive");
  };

  return (
    <>
      <Seo title={t("nps.seoTitle")} />
      <div className="min-h-screen bg-gradient-to-br from-ek-canvas via-white to-ek-purple/5 text-ek-ink">
        <main className="mx-auto max-w-xl px-6 py-20" data-testid="nps-landing-page">
          <p className="text-xs uppercase tracking-[0.2em] text-ek-purple font-semibold">
            {t("nps.brandEyebrow")}
          </p>
          <h1 className="mt-2 font-display text-3xl font-bold">
            {t("nps.heroTitle")}
          </h1>

          {posting && (
            <p className="mt-6 inline-flex items-center gap-2 text-sm text-ek-ink/60">
              <Loader2 className="h-4 w-4 animate-spin" /> {t("nps.recording")}
            </p>
          )}

          {submitted && (
            <div className="mt-6 space-y-4" data-testid="nps-confirmation">
              <div className="rounded-2xl border border-ek-purple/20 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-3">
                  {submitted.category === "promoter"
                    ? <ThumbsUp className="h-6 w-6 text-emerald-500" />
                    : <Smile className="h-6 w-6 text-ek-purple" />}
                  <div>
                    <p className="text-sm text-ek-ink/60">{t("nps.yourScore")}</p>
                    <p className="text-3xl font-bold">{submitted.score} / 10</p>
                  </div>
                  <span
                    data-testid="nps-category"
                    className={`ml-auto rounded-full px-3 py-1 text-xs font-semibold ${
                      submitted.category === "promoter" ? "bg-emerald-100 text-emerald-800"
                      : submitted.category === "passive" ? "bg-slate-100 text-slate-700"
                      : "bg-rose-100 text-rose-800"}`}
                  >
                    {categoryLabel(submitted.category)}
                  </span>
                </div>

                {submitted.category === "promoter" && (
                  <p className="mt-4 text-sm text-ek-ink/70">
                    {t("nps.promoterCopyBefore")}
                    <a href="mailto:info@ekselens.com" className="text-ek-purple underline">
                      info@ekselens.com
                    </a>
                    {t("nps.promoterCopyAfter")}
                  </p>
                )}
                {submitted.category === "passive" && (
                  <p className="mt-4 text-sm text-ek-ink/70">
                    {t("nps.passiveCopy", { score: submitted.score })}
                  </p>
                )}
                {submitted.category === "detractor" && (
                  <p className="mt-4 text-sm text-ek-ink/70">
                    {t("nps.detractorCopy")}
                  </p>
                )}
              </div>

              {!showComment ? (
                <button
                  onClick={() => setShowComment(true)}
                  className="inline-flex items-center gap-2 rounded-lg border border-ek-purple/30 px-4 py-2 text-sm font-semibold text-ek-purple hover:bg-ek-purple/5"
                  data-testid="nps-add-comment-btn"
                >
                  <MessageSquare className="h-4 w-4" /> {t("nps.addComment")}
                </button>
              ) : (
                <div className="rounded-2xl border border-ek-purple/20 bg-white p-4">
                  <textarea
                    autoFocus
                    rows="3"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={t("nps.commentPlaceholder")}
                    maxLength={1000}
                    className="w-full rounded-md border border-black/10 px-3 py-2 text-sm"
                    data-testid="nps-comment"
                  />
                  <div className="mt-2 flex justify-end gap-2">
                    <button
                      onClick={() => { setShowComment(false); setComment(""); }}
                      className="rounded-md px-3 py-1.5 text-xs text-ek-ink/60 hover:text-ek-ink"
                    >
                      {t("nps.skip")}
                    </button>
                    <button
                      onClick={sendComment}
                      disabled={!comment.trim() || posting}
                      className="rounded-md bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                      data-testid="nps-send-comment"
                    >
                      {t("nps.send")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {score === null && !submitted && (
            <div className="mt-6 rounded-2xl border border-black/5 bg-white p-6 shadow-sm">
              <p className="text-sm text-ek-ink/70">
                {t("nps.pickerPrompt")}
              </p>
              <div className="mt-4 grid grid-cols-11 gap-1">
                {Array.from({ length: 11 }, (_, n) => (
                  <button
                    key={n}
                    onClick={() => { setScore(n); submit(n); }}
                    className={`h-11 rounded-md font-semibold text-sm transition ${
                      n <= 6 ? "bg-slate-100 hover:bg-rose-100 text-ek-ink"
                      : n <= 8 ? "bg-amber-100 hover:bg-amber-200 text-amber-900"
                      : "bg-emerald-100 hover:bg-emerald-200 text-emerald-900"}`}
                    data-testid={`nps-score-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-ek-ink/50">
                {t("nps.pickerLegend")}
              </p>
            </div>
          )}
        </main>
      </div>
    </>
  );
}
